import React, {useEffect, useState, useRef} from 'react'
import {Video, CtaButton} from '_uiComponents'
import {VStack, Box, Text, useMultiStyleConfig, Link} from '@chakra-ui/react'
import {updateCloudinaryPDVideo, updateCloudinaryPDPoster, createRedirectUrl, PAGE_DESIGNER_MODULES} from '_utils'
import {usePageDesigner, useIsElementInViewport, useAnalytics, useViewport, useOpenDrawer} from '_customHooks'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {getProductAnalyticsInfo, getProductImpressionAnalytics} from '_analytics/utils/analyticsUtils'
import {CLDVideoAsset} from '_types'

import AOS from 'aos'
import 'aos/dist/aos.css'

export interface WallMixedVideoProps {
    iframe_id?: CLDVideoAsset
    iframem_id?: CLDVideoAsset
    video_link?: string
    title?: string
    text?: string
    text_align?: string
    h_align?: string
    v_align?: string
    ctaText?: string
    ctaStyle?: 'button' | 'text' | 'text-cta' | 'outline' | 'blank'
    animation_enabled?: boolean
    animation_cta_text?: boolean
    animation_text?: boolean
    animation_duration?: string
    video_padding?: string
    button_background?: string
    layout_text_below_image: boolean
    content_color?: string
    button_color?: string
    productID?: string
    position: number
    analyticsID?: string
    analyticsList?: string
    listingLayout?: string
    mobileSideBySide?: boolean
    analyticsName?: string
}

const WallMixedVideo: React.FC<WallMixedVideoProps> = ({
    iframe_id,
    iframem_id,
    title,
    text,
    text_align,
    h_align,
    v_align,
    ctaText,
    ctaStyle,
    animation_enabled,
    animation_cta_text,
    animation_text,
    animation_duration,
    video_padding,
    button_background,
    layout_text_below_image,
    content_color,
    button_color,
    productID,
    video_link,
    position,
    analyticsID,
    analyticsList,
    listingLayout,
    mobileSideBySide,
    analyticsName,
}) => {
    const {linkObject, globalVideoTransformation, CloudinaryPageDesignerCNAME} = usePageDesigner()
    const {handleShopTheImageClick} = useOpenDrawer()
    const [alreadySentPromoView, setAlreadySentPromoView] = useState(false)
    const [alreadySentProductImpression, setAlreadySentProductImpression] = useState(false)
    const lastMilestoneRef = useRef(0)
    const milestones = [25, 50, 75, 100]
    const targetElement = useRef(null)
    const isVisible = useIsElementInViewport(targetElement)
    const {isMobile} = useViewport()
    const {analyticsEventPromoView, analyticsEventPromoClick, analyticsProductClick, analyticsProductImpression, analyticsEventVideo} = useAnalytics()
    const {data: product} = useProduct(
        {
            parameters: {
                id: productID!,
            },
        },
        {
            enabled: !!productID,
        },
    )
    const [moduleLink, setModuleLink] = useState(video_link ? createRedirectUrl(video_link, linkObject.hostlink, linkObject.locale, linkObject.site) : '')
    useEffect(() => {
        if (product && product.slugUrl) {
            setModuleLink(createRedirectUrl(product.slugUrl, linkObject.hostlink, linkObject.locale, linkObject.site))
        }
    }, [product])

    const ratioDesktop =
        iframe_id?.formValues?.video?.asset?.height && iframe_id?.formValues?.video?.asset?.width
            ? iframe_id.formValues.video.asset.width / iframe_id.formValues.video.asset.height
            : 0

    const ratioMobile =
        iframem_id?.formValues?.video?.asset?.height && iframem_id?.formValues?.video?.asset?.width
            ? iframem_id.formValues.video.asset.width / iframem_id.formValues.video.asset.height
            : 0

    // analytics
    useEffect(() => {
        if (isVisible && !alreadySentPromoView) {
            const analyticsCheck = productID ? productID : analyticsID
            analyticsEventPromoView({
                id: analyticsCheck ? analyticsCheck : 'null',
                position: position,
                creative: PAGE_DESIGNER_MODULES.WALL_MIXED_VIDEO,
                name: analyticsName ? analyticsName : 'null',
            })
            setAlreadySentPromoView(true)
        }
    }, [isVisible])

    useEffect(() => {
        if (isVisible && !alreadySentProductImpression && product) {
            const {productCurrency} = getProductAnalyticsInfo(product)
            const analyticsProductObject = getProductImpressionAnalytics({product, analyticsList: analyticsList, idx: position - 1})

            analyticsProductImpression({
                currencyCode: productCurrency,
                template: listingLayout,
                impressions: analyticsProductObject,
            })

            setAlreadySentProductImpression(true)
        }
    }, [isVisible, product])

    const triggerAnalyticsEventProductClick = (): void => {
        if (product) {
            const {productCurrency} = getProductAnalyticsInfo(product)
            const analyticsProductObject = getProductImpressionAnalytics({product, analyticsList: analyticsList, idx: position - 1})

            analyticsProductClick({
                currencyCode: productCurrency,
                dimension25: listingLayout,
                actionField: {
                    list: analyticsList ? analyticsList : '',
                },
                product: analyticsProductObject,
            })
        }
    }

    const triggerAnalyticsEventPromoClick = (textBtn: string, positionBtn?: string): void => {
        const analyticsCheck = productID ? productID : analyticsID
        analyticsEventPromoClick({
            id: analyticsCheck ? analyticsCheck : 'null',
            position: positionBtn ? String(position) + '-' + positionBtn : String(position),
            creative: PAGE_DESIGNER_MODULES.WALL_MIXED_VIDEO,
            promoText: textBtn,
            name: analyticsName ? analyticsName : 'null',
        })
    }

    const handlePlay = () => {
        if (isVisible) {
            analyticsEventVideo({
                eventName: 'video',
                action: 'play',
                category: 'video',
            })
        }
    }
    function getVideoProgressAction(milestone: number): string | undefined {
        switch (milestone) {
            case 25:
                return 'progress - 25%'
            case 50:
                return 'progress - 50%'
            case 75:
                return 'progress - 75%'
            case 100:
                return 'progress - 100%'
            default:
                return undefined
        }
    }

    const handleTimeUpdate = (event: React.SyntheticEvent<HTMLVideoElement>) => {
        const video = event.target as HTMLVideoElement
        if (!video.duration) return
        const percent = (video.currentTime / video.duration) * 100
        const nextMilestone = milestones.find((mark) => percent >= mark - 5 && percent <= mark + 5 && mark > lastMilestoneRef.current)

        if (nextMilestone) {
            lastMilestoneRef.current = nextMilestone
            if (isVisible) {
                const action = getVideoProgressAction(nextMilestone)
                if (action) {
                    analyticsEventVideo({
                        eventName: 'video',
                        action,
                        category: 'video',
                    })
                }
            }
            if (nextMilestone === 100) {
                lastMilestoneRef.current = 0
            }
        }
    }

    const getPaddingValues = (video_padding?: string) => {
        if (!video_padding) return {top: 0, right: 0, bottom: 0, left: 0}
        const parts = video_padding.split(' ').map((v) => parseInt(v.replace('px', ''), 10) || 0)
        if (parts.length === 1) {
            return {top: parts[0], right: parts[0], bottom: parts[0], left: parts[0]}
        }
        if (parts.length === 2) {
            return {top: parts[0], right: parts[1], bottom: parts[0], left: parts[1]}
        }
        return {
            top: parts[0],
            right: parts[1],
            bottom: parts[2] || parts[0],
            left: parts[3] || parts[1],
        }
    }
    // animation_enabled it's for image
    // animation_text animation for title
    // animation_cta_text animation for cta only if his style is text
    useEffect(() => {
        if (animation_enabled || animation_text || animation_cta_text) {
            AOS.init()
        }
    }, [])
    const videoPadding = getPaddingValues(video_padding)

    const styles = useMultiStyleConfig('WallMixedVideo', {
        text_align,
        h_align,
        v_align,
        videoPadding,
        button_background,
        content_color,
        button_color,
        layout_text_below_image,
        ratioDesktop,
        ratioMobile,
        mobileSideBySide,
    }) as Record<string, any>
    const playerDeskConf = iframe_id && JSON.parse(iframe_id.playerConf)
    const playerMobileConf = iframem_id && JSON.parse(iframem_id.playerConf)

    const isControlDesk = playerDeskConf && playerDeskConf.playerConfig?.controls
    const isAutoplayDesk = playerDeskConf && playerDeskConf.playerConfig?.autoplay
    const isLoopDesk = playerDeskConf && playerDeskConf.playerConfig?.loop
    const isMuteDesk = playerDeskConf && playerDeskConf.playerConfig?.muted
    const isControlMobile = playerMobileConf && playerMobileConf.playerConfig?.controls
    const isAutoplayMobile = playerMobileConf && playerMobileConf.playerConfig?.autoplay
    const isLoopMobile = playerMobileConf && playerMobileConf.playerConfig?.loop
    const isMuteMobile = playerMobileConf && playerMobileConf.playerConfig?.muted

    const isModuleClickable = !!moduleLink && ((isMobile && (playerMobileConf ? !isControlMobile : !isControlDesk)) || (!isMobile && !isControlDesk))

    const ctaVariantMap = {
        button: 'button',
        outline: 'outline',
        text: 'textBorderTop',
        'text-cta': 'text',
    } as const
    const ctaVariant = ctaStyle && ctaStyle in ctaVariantMap ? ctaVariantMap[ctaStyle] : undefined

    const isWrappedLink = !layout_text_below_image

    const getCtaColor = (): string => {
        if (layout_text_below_image) {
            return ctaStyle === 'button' ? 'color.text.white' : 'color.text.black'
        } else {
            return 'color.text.white'
        }
    }

    const TitleBlock = (
        <VStack {...(layout_text_below_image ? styles.titleContainerBelow : styles.titleContainer)}>
            {title && (
                <Text
                    as="h2"
                    {...(layout_text_below_image ? styles.titleBelow : styles.title)}
                    {...(animation_text
                        ? {
                              'data-aos': 'fade-left',
                              'data-aos-duration': animation_duration ? animation_duration : '1200',
                          }
                        : {})}
                >
                    {title}
                </Text>
            )}
            {text && <Text {...(layout_text_below_image ? styles.textBelow : styles.text)}>{text}</Text>}
            {ctaStyle !== 'blank' && ctaText && (
                <Box>
                    {/* empty wrapper fixes multi-line CTA text underline */}
                    <CtaButton
                        variant={ctaVariant}
                        href={isWrappedLink ? '' : moduleLink}
                        ctaColor={getCtaColor()}
                        background="color.text.black"
                        onClick={(e) => {
                            if (layout_text_below_image) {
                                if (handleShopTheImageClick(e, video_link)) return
                                if (productID && product) triggerAnalyticsEventProductClick()
                                triggerAnalyticsEventPromoClick('')
                            }
                        }}
                        {...(animation_text
                            ? {
                                  'data-aos': 'animationCtaText',
                                  'data-aos-offset': '0',
                                  'data-aos-once': 'false',
                              }
                            : {})}
                    >
                        {ctaText}
                    </CtaButton>
                </Box>
            )}
        </VStack>
    )

    // Always render the link to keep SSR/CSR markup consistent.
    const WrapperLink: React.FC<{children: React.ReactNode}> = ({children}) => (
        <Link
            data-testid="wall-mixed-video-full-link"
            className="cc-link-full"
            href={moduleLink || undefined}
            title={''}
            aria-label={'wall banner link'}
            data-disabled={!isModuleClickable}
            {...styles.link}
            onClick={(e) => {
                if (!isModuleClickable) {
                    e.preventDefault()
                    return
                }
                if (handleShopTheImageClick(e, video_link)) return
                if (productID && product) triggerAnalyticsEventProductClick()
                triggerAnalyticsEventPromoClick('')
            }}
        >
            {children}
        </Link>
    )

    return (
        <WrapperLink>
            <VStack {...styles.boxWrapper} data-testid="wall-mixed-video-wrapper" ref={targetElement}>
                <Box {...styles.videoWrapper}>
                    {isMobile ? (
                        <Video
                            overflow="hidden"
                            height="100%"
                            isVideoModule={true}
                            autoplay={playerMobileConf ? isAutoplayMobile : isAutoplayDesk}
                            controls={playerMobileConf ? isControlMobile : isControlDesk}
                            muted={playerMobileConf ? isMuteMobile : isMuteDesk}
                            loop={playerMobileConf ? isLoopMobile : isLoopDesk}
                            videoxs={iframem_id ? updateCloudinaryPDVideo(iframem_id, globalVideoTransformation, CloudinaryPageDesignerCNAME) : ''}
                            videolg={iframe_id ? updateCloudinaryPDVideo(iframe_id, globalVideoTransformation, CloudinaryPageDesignerCNAME) : ''}
                            poster={
                                (iframem_id ? updateCloudinaryPDPoster(iframem_id, globalVideoTransformation, CloudinaryPageDesignerCNAME) : '') ||
                                (iframe_id ? updateCloudinaryPDPoster(iframe_id, globalVideoTransformation, CloudinaryPageDesignerCNAME) : '')
                            }
                            objectFit="cover"
                            playVideofunc={handlePlay}
                            onTimeUpdateFunc={handleTimeUpdate}
                            {...styles.video}
                        ></Video>
                    ) : (
                        <Video
                            overflow="hidden"
                            height="100%"
                            isVideoModule={true}
                            autoplay={isAutoplayDesk}
                            controls={isControlDesk}
                            muted={isMuteDesk}
                            loop={isLoopDesk}
                            videolg={iframe_id ? updateCloudinaryPDVideo(iframe_id, globalVideoTransformation, CloudinaryPageDesignerCNAME) : ''}
                            poster={iframe_id ? updateCloudinaryPDPoster(iframe_id, globalVideoTransformation, CloudinaryPageDesignerCNAME) : ''}
                            objectFit="cover"
                            playVideofunc={handlePlay}
                            onTimeUpdateFunc={handleTimeUpdate}
                            {...styles.video}
                        ></Video>
                    )}
                </Box>
                {<>{TitleBlock}</>}
            </VStack>
        </WrapperLink>
    )
}

const MemoizedWallMixedVideo = React.memo(WallMixedVideo)

export {MemoizedWallMixedVideo as WallMixedVideo}
